import React from 'react';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Autocomplete, Alert } from '@mui/material';
import {
  DialogContent,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Collapse,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LocalizedStrings from 'react-localization';
import { localisedText } from '../../../lib/localisation.es6';
import { PRIMARY_THEME_COLOR_SHADE_1 } from '../../../lib/constants';
import uniqueCodes from '../../../containers/SmsTemplate/template_codes';

const labels = new LocalizedStrings(localisedText);

export class AddEditSubscription extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      guid: null,
      event_types: [],
      callback_url: '',
      require_customer_data: false,
      require_policy_data: false,
      require_claim_data: false,
      require_payment_data: false,
      data_transform: '',
      errors: {},
      showAdvanced: false,
      ssrfWarning: '',
    };
  }

  componentDidMount() {
    this.populateFromSubscription(this.props.subscription);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.subscription !== this.props.subscription) {
      this.populateFromSubscription(this.props.subscription);
    }
  }

  // ── Hydration ──────────────────────────────────────────────────────────────

  populateFromSubscription(subscription) {
    if (!subscription) {
      this.setState({
        guid: null,
        event_types: [],
        callback_url: '',
        require_customer_data: false,
        require_policy_data: false,
        require_claim_data: false,
        require_payment_data: false,
        data_transform: '',
        errors: {},
        ssrfWarning: '',
        showAdvanced: false,
      });
      return;
    }

    const event_types = Array.isArray(subscription.event_types)
      ? subscription.event_types
      : String(subscription.event_types || '').split(',').map((s) => s.trim()).filter(Boolean);

    let data_transform = '';
    if (subscription.data_transform) {
      try {
        data_transform = typeof subscription.data_transform === 'string'
          ? subscription.data_transform
          : JSON.stringify(subscription.data_transform, null, 2);
      } catch (_) {}
    }

    this.setState({
      guid: subscription.guid,
      event_types,
      callback_url: subscription.callback_url || '',
      require_customer_data: subscription.require_customer_data || false,
      require_policy_data: subscription.require_policy_data || false,
      require_claim_data: subscription.require_claim_data || false,
      require_payment_data: subscription.require_payment_data || false,
      data_transform,
      errors: {},
      ssrfWarning: '',
      showAdvanced: !!data_transform,
    });
  }

  // ── Validation ─────────────────────────────────────────────────────────────

  validateForm = () => {
    const errors = {};
    if (!this.state.event_types || this.state.event_types.length === 0) {
      errors.event_types = labels.required || 'This field is required';
    }
    if (!this.state.callback_url.trim()) {
      errors.callback_url = labels.required || 'This field is required';
    }
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  // ── Handlers ───────────────────────────────────────────────────────────────

  handleCallbackUrlChange = (e) => {
    const url = e.target.value;
    this.setState({
      callback_url: url,
      ssrfWarning: url && !url.startsWith('https://')
        ? (labels.webhookUrlMustBeHttps || 'Callback URL must use HTTPS protocol')
        : '',
    });
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  handleSubmit = () => {
    if (!this.validateForm()) return;

    const event_types = this.state.event_types;

    let data_transform;
    if (this.state.data_transform && this.state.data_transform.trim()) {
      try {
        data_transform = JSON.parse(this.state.data_transform);
      } catch (_) {
        this.setState((prev) => ({
          errors: { ...prev.errors, data_transform: 'Invalid JSON in Data Transform' },
        }));
        return;
      }
    }

    const payload = {
      event_types,
      callback_url: this.state.callback_url,
      require_customer_data: this.state.require_customer_data,
      require_policy_data: this.state.require_policy_data,
      require_claim_data: this.state.require_claim_data,
      require_payment_data: this.state.require_payment_data,
      ...(data_transform !== undefined && { data_transform }),
    };

    const isEdit = !!this.state.guid;
    if (isEdit) payload.guid = this.state.guid;

    this.props.onSave(payload, isEdit);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  render() {
    const { open, onClose, loading, subscription, errors: propErrors } = this.props;
    const {
      guid, event_types = [], callback_url, errors, ssrfWarning, showAdvanced,
      require_customer_data, require_policy_data, require_claim_data, require_payment_data,
      data_transform,
    } = this.state;
    const isEdit = !!subscription;

    return (
      <Dialog open={open} onClose={onClose} fullScreen>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar style={{ background: PRIMARY_THEME_COLOR_SHADE_1 }}>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {isEdit ? labels.editWebhook || 'Edit Webhook' : labels.addWebhook || 'Add Webhook'}
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent>
          <div style={{ marginTop: '2%' }}>
            {propErrors && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {typeof propErrors === 'string' ? propErrors : JSON.stringify(propErrors)}
              </Alert>
            )}

            <Grid container spacing={2}>
              {isEdit && guid && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    {labels.guid || 'GUID'}: {guid}
                  </Typography>
                </Grid>
              )}

              {/* Event Types */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={uniqueCodes}
                  value={event_types}
                  onChange={(event, newValue) => {
                    this.setState({ event_types: newValue });
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    // When the user types a value and presses comma, treat it as a new tag
                    if (reason === 'input' && newInputValue.endsWith(',')) {
                      const trimmed = newInputValue.slice(0, -1).trim();
                      if (trimmed) {
                        this.setState((prev) => ({
                          event_types: [...prev.event_types, trimmed],
                        }));
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={labels.eventTypes || 'Event Types'}
                      placeholder={event_types.length === 0 ? 'Type or select event codes...' : ''}
                      variant="outlined"
                      error={!!errors.event_types}
                      helperText={errors.event_types || labels.eventTypesHelp || 'Select from list or type a custom code and press Enter'}
                    />
                  )}
                />
              </Grid>

              {/* Callback URL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={labels.webhookUrl || 'Callback URL'}
                  type="url"
                  value={callback_url}
                  onChange={this.handleCallbackUrlChange}
                  variant="outlined"
                  error={!!errors.callback_url}
                  helperText={errors.callback_url}
                />
                {ssrfWarning && (
                  <Alert severity="warning" style={{ marginTop: 4 }}>{ssrfWarning}</Alert>
                )}
              </Grid>

              {/* Data Enrichment */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                  {labels.dataEnrichment || 'Data Enrichment'}
                </Typography>
                <Grid container>
                  {[
                    { field: 'require_customer_data', label: labels.includeCustomerData || 'Include Customer Data', value: require_customer_data },
                    { field: 'require_policy_data',   label: labels.includePolicyData   || 'Include Policy Data',   value: require_policy_data },
                    { field: 'require_claim_data',    label: labels.includeClaimData    || 'Include Claim Data',    value: require_claim_data },
                    { field: 'require_payment_data',  label: labels.includePaymentData  || 'Include Payment Data',  value: require_payment_data },
                  ].map(({ field, label, value }) => (
                    <Grid item xs={12} sm={6} md={3} key={field}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={value}
                            onChange={() => this.setState({ [field]: !this.state[field] })}
                            color="primary"
                          />
                        }
                        label={label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Advanced: Data Transform */}
              <Grid item xs={12}>
                <Box mb={1}>
                  <Button
                    size="small"
                    startIcon={<ExpandMoreIcon />}
                    onClick={() => this.setState((prev) => ({ showAdvanced: !prev.showAdvanced }))}
                  >
                    {showAdvanced ? labels.hideAdvanced || 'Hide Advanced' : labels.showAdvanced || 'Show Advanced'}
                  </Button>
                </Box>
                <Collapse in={showAdvanced}>
                  <TextField
                    fullWidth multiline rows={6}
                    label={labels.dataTransform || 'Data Transform (JSON Template)'}
                    value={data_transform}
                    onChange={(e) => this.setState({ data_transform: e.target.value })}
                    // eslint-disable-next-line no-template-curly-in-string
                    placeholder='{"customer_name": "${payload.customer_name}", "policy_guid": "${payload.policy_guid}"}'
                    variant="outlined"
                    error={!!errors.data_transform}
                    // eslint-disable-next-line no-template-curly-in-string
                    helperText={errors.data_transform || labels.dataTransformHelp || 'Optional JSON template with ${expression} placeholders'}
                  />
                </Collapse>
              </Grid>

              {/* Action buttons */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" style={{ gap: 8, marginTop: 8 }}>
                  <Button onClick={onClose} disabled={loading}>
                    {labels.cancel || 'Cancel'}
                  </Button>
                  <Button
                    onClick={this.handleSubmit}
                    disabled={loading}
                    color="primary"
                    variant="contained"
                    style={{ background: loading ? undefined : PRIMARY_THEME_COLOR_SHADE_1 }}
                  >
                    {loading
                      ? <CircularProgress size={24} />
                      : (isEdit ? labels.update || 'Update' : labels.save || 'Save')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}
